import React, { useEffect } from "react";
import { X } from "lucide-react";

// Full-screen lightbox that embeds a YouTube video on-site (no redirect).
export default function VideoModal({ videoId, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  if (!videoId) return null;

  return (
    <div
      data-testid="video-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-deep/90 backdrop-blur-sm p-4 animate-in fade-in-0 duration-200"
      onClick={onClose}
    >
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="video-modal-close"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-12 right-0 md:-right-2 w-10 h-10 rounded-full bg-white/10 hover:bg-orange text-white flex items-center justify-center transition-colors"
        >
          <X size={20} />
        </button>
        <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
          <iframe
            data-testid="video-modal-iframe"
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={title || "Video"}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {title && <p className="text-center text-white/80 text-sm mt-4 font-semibold">{title}</p>}
      </div>
    </div>
  );
}

// Thumbnail with a fallback chain: provided image -> YouTube maxres -> YouTube hqdefault -> series thumbnail.
export function VideoThumb({ src, youtubeId, alt, className, fallback }) {
  const candidates = [];
  if (src) candidates.push(src);
  if (youtubeId) {
    candidates.push(`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`);
    candidates.push(`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`);
  }
  if (fallback) candidates.push(fallback);
  const handleError = (e) => {
    const next = Number(e.target.dataset.idx || 0) + 1;
    if (next < candidates.length) {
      e.target.dataset.idx = String(next);
      e.target.src = candidates[next];
    }
  };
  return <img src={candidates[0] || ""} data-idx="0" alt={alt} className={className} onError={handleError} loading="lazy" />;
}
